import { useEffect, useState } from 'react';

import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { ErrorState, LoadingState } from '@/shared/ui';

import { usePricingConfig, useUpsertPricingConfig } from '../../hooks';
import { FAILOVER_POLICY_OPTIONS, PRICING_MODE_OPTIONS } from '../../model/options';
import type { FailoverPolicy, PricingMode } from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';

interface PanelProps {
  canManage: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/** Tenant pricing configuration — engine mode, provider, failover policy. */
export function PricingConfigPanel({ canManage, onResult }: PanelProps) {
  const query = usePricingConfig();
  const save = useUpsertPricingConfig();

  const [mode, setMode] = useState<PricingMode>('INTERNAL');
  const [providerConfigId, setProviderConfigId] = useState('');
  const [failover, setFailover] = useState<FailoverPolicy>('FALLBACK_TO_INTERNAL');

  useEffect(() => {
    const data = query.data;
    if (!data) return;
    setMode((data.pricingMode as PricingMode) ?? 'INTERNAL');
    setProviderConfigId(data.providerConfigId ?? '');
    setFailover((data.failoverPolicy as FailoverPolicy) ?? 'FALLBACK_TO_INTERNAL');
  }, [query.data]);

  // A 404 just means "not configured yet" — show the form with defaults.
  const notConfigured = query.isError && query.error?.status === 404;
  if (query.isLoading) return <LoadingState label="Загрузка конфигурации…" />;
  if (query.isError && !notConfigured) {
    return (
      <ErrorState
        title="Не удалось загрузить конфигурацию"
        message={query.error?.message ?? 'Произошла ошибка.'}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const submit = () => {
    save.mutate(
      {
        pricingMode: mode,
        providerConfigId: providerConfigId.trim() || undefined,
        failoverPolicy: failover,
      },
      {
        onSuccess: () => onResult('success', 'Конфигурация цен сохранена.'),
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Конфигурация ценообразования
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Как для тенанта вычисляются цены: внутренний движок, внешний провайдер или гибрид.
        </Typography>
        {notConfigured ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Конфигурация ещё не задана — заданы значения по умолчанию.
          </Alert>
        ) : null}
        <Stack spacing={2.5} sx={{ maxWidth: 520 }}>
          <TextField
            select
            label="Режим ценообразования"
            value={mode}
            onChange={(e) => setMode(e.target.value as PricingMode)}
            disabled={!canManage}
            fullWidth
          >
            {PRICING_MODE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Политика отказоустойчивости"
            value={failover}
            onChange={(e) => setFailover(e.target.value as FailoverPolicy)}
            disabled={!canManage}
            fullWidth
          >
            {FAILOVER_POLICY_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="ID конфигурации провайдера"
            value={providerConfigId}
            onChange={(e) => setProviderConfigId(e.target.value)}
            disabled={!canManage}
            fullWidth
            helperText="Необязательно — для режимов с внешним провайдером."
            slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
          />
          {canManage ? (
            <Button
              variant="contained"
              onClick={submit}
              disabled={save.isPending}
              startIcon={save.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
              sx={{ alignSelf: 'flex-start' }}
            >
              Сохранить
            </Button>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}
